import React, {
  FC,
  memo,
  NamedExoticComponent,
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react'
import {
  useDrawerFilters,
  PHARMACIST_FILTER_ALL_VALUE,
} from '@/components/organisms/useDrawerFilters'
import { type UseDrawerSelectionModeReturn } from '@/features/yakureki'

import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import {
  Stack,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  IconButton,
  Typography,
  Tooltip,
  Switch,
  Menu,
  MenuItem,
  ListItemIcon,
  Select,
  FormControl,
} from '@mui/material'
// formatTimeInJP は患者カード簡素化に伴い不要（時間表示を削除）
import { Yakureki, isInProgressStatus } from '@/shared/types/yakurekiCore'
import { patchMarkDismissed, patchUndoDismissed } from '@/shared/apis/yakureki'
import { getRecordingTooltipText } from '@/shared/constants/const'
import { formatDateTimeToDate } from '@/shared/helpers/formatDateTimeToDate'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import SearchIcon from '@mui/icons-material/Search'
import TuneIcon from '@mui/icons-material/Tune'
import RefreshIcon from '@mui/icons-material/Refresh'
import MicIcon from '@mui/icons-material/Mic'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import DoneIcon from '@mui/icons-material/Done'
import Checkbox from '@mui/material/Checkbox'
import { SearchModalBody } from '@/components/templates/SearchModalBody'
import { SettingModalBody } from '@/apps/legacy/components/templates/SettingModalBody'

// 完了表示トグルの定数
const SHOW_DISMISSED_LABEL = '完了を表示' as const

// 薬剤師フィルタの表示ラベル
const PHARMACIST_FILTER_LABEL = '担当' as const

// ドロワーメニューの色定数
const DRAWER_COLORS = {
  background: 'rgba(227, 242, 253, 0.95)',
  backgroundSolid: 'rgba(227, 242, 253, 0.9)',
  patientNormal: colorTokens.patient.normal,
  patientSelected: colorTokens.blue[200],
  patientHover: colorTokens.patient.hover,
  text: colorTokens.text.primary,
  textSecondary: colorTokens.text.secondary,
  textSelected: colorTokens.blue[600],
  border: colorTokens.blue[100],
  borderTransparent: 'rgba(187, 222, 251, 0.8)',
  iconDisabled: colorTokens.border.light,
  shadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
} as const

// ドロワーメニューのスタイル定義
const drawerStyles = {
  paper: {
    backgroundColor: DRAWER_COLORS.background,
    backdropFilter: 'blur(10px)',
    boxShadow: DRAWER_COLORS.shadow,
    borderRight: `1px solid ${DRAWER_COLORS.borderTransparent}`,
  },
  listItem: (isSelected: boolean) => ({
    backgroundColor: isSelected
      ? DRAWER_COLORS.patientSelected
      : DRAWER_COLORS.patientNormal,
    color: isSelected ? DRAWER_COLORS.textSelected : DRAWER_COLORS.text,
    borderBottom: `1px solid ${DRAWER_COLORS.border}`,
    '&:hover': {
      backgroundColor: isSelected
        ? DRAWER_COLORS.patientSelected
        : DRAWER_COLORS.patientHover,
    },
    transition: 'all 0.2s ease',
  }),
} as const

// ドロワー内の小ボタン共通スタイル（完了・戻す・まとめて完了）
const drawerButtonStyle = {
  minWidth: 'auto',
  px: 1,
  py: 0.25,
  borderRadius: 1,
  color: colorTokens.blue[600],
  backgroundColor: colorTokens.blue[50],
  borderColor: colorTokens.blue[300],
  '&:hover': {
    backgroundColor: colorTokens.blue[100],
    borderColor: colorTokens.blue[500],
  },
} as const

// アイコン色決定ロジック
const getIconColor = (isDisabled: boolean) =>
  isDisabled ? DRAWER_COLORS.iconDisabled : DRAWER_COLORS.textSecondary

type Props = {
  isOpen: boolean
  onClose: () => void
  yakurekiList: Yakureki[]
  selectedYakureki?: Yakureki
  selectYakureki: (yakurakiId: string) => void
  BottomButtons: React.ReactNode[]
  resetYakurekiList: () => Promise<void>
  onRecordClick?: () => void
  // 患者選択モード (まとめて完了 / まとめて録音 を discriminated union で一元管理).
  // selection が必須である以上、selection を消費する callback / ヘルパも対称に必須.
  selection: UseDrawerSelectionModeReturn
  onStartGroupRecording: () => void
  // まとめて完了の確定処理 (患者 ID を受け取り API 並列呼び出し + リロードを行う)
  onConfirmBatchDismiss: (ids: string[]) => Promise<void>
  isSelectableForGroup: (patient: Yakureki) => boolean
  // 薬剤師選択必須: 未選択時はリストをグレーアウト
  isPharmacistSelected?: boolean
}

export const DrawerPrescList: FC<Props> = ({
  isOpen,
  onClose,
  yakurekiList,
  selectedYakureki,
  selectYakureki,
  BottomButtons,
  resetYakurekiList,
  onRecordClick,
  selection,
  onStartGroupRecording,
  onConfirmBatchDismiss,
  isSelectableForGroup,
  isPharmacistSelected = true,
}) => {
  // 選択患者への自動スクロール用 ref
  const selectedItemRef = useRef<HTMLDivElement>(null)

  // 対応完了（dismissed）: APIでステータス更新 → リスト再取得
  const handleMarkDone = useCallback(
    async (yakurekiId: string) => {
      try {
        await patchMarkDismissed(yakurekiId)
        await resetYakurekiList()
      } catch {
        // APIエラー時はリロードのみ試行
      }
    },
    [resetYakurekiList]
  )

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        })
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // フィルタ処理（日付・薬剤師・完了表示）を統合hookで管理
  const {
    filteredList: dateFilteredYakurekiList,
    dismissedCount,
    date,
    isBackDisabled,
    isForwardDisabled,
    isToday,
    handleDateBack,
    handleDateForward,
    handleJumpToToday,
    showDismissed,
    handleShowDismissedToggle,
    pharmacistData,
    filterPharmacistId,
    handlePharmacistFilterChange,
  } = useDrawerFilters(yakurekiList)

  // 完了取り消し（dismissed → copied に戻す）
  const handleUndoDismiss = useCallback(
    async (yakurekiId: string) => {
      try {
        await patchUndoDismissed(yakurekiId)
        await resetYakurekiList()
      } catch {
        // エラー時はリロードのみ
      }
    },
    [resetYakurekiList]
  )

  // --- ここから検索モーダル関連の追加 ---
  // 検索モーダルの開閉状態と検索文言を管理する
  const [searchOpen, setSearchOpen] = useState(false)
  const [settingOpen, setSettingOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // 録音メニュー（MicIcon押下時）
  const [micMenuAnchor, setMicMenuAnchor] = useState<null | HTMLElement>(null)
  const isMicMenuOpen = Boolean(micMenuAnchor)

  // Tooltip text calculation for recording button
  const isNsipsMode = localStorageWrapper.getItem('IsUseNsips') || false
  const isProgress = selectedYakureki
    ? isInProgressStatus(selectedYakureki.status)
    : false
  const tooltipText = getRecordingTooltipText(isProgress, isNsipsMode)

  // 検索モーダルを開く
  const handleSearchOpen = () => {
    setSearchOpen(true)
    setSearchQuery('') // 新しく開くごとにリセットする場合
  }

  const handleSettingOpen = () => {
    setSettingOpen(true)
  }

  // 検索モーダルを閉じる
  const onCloseSearchModal = () => {
    setSearchOpen(false)
    onClose()
  }

  const onCloseSettingModal = () => {
    setSettingOpen(false)
    onClose()
  }

  const handleResetYakurekiList = async () => {
    const confirmed = window.confirm(
      '全体のリロードにはやや時間がかかります。本当に実施しますか？'
    )
    if (confirmed) {
      await resetYakurekiList()
    }
  }

  // 検索クエリに対して表示するリストをメモ化して作成
  // searchQuery が空の場合は全件、そうでない場合は patient_name / label の部分一致検索
  const filteredYakurekiList = useMemo(() => {
    if (!searchQuery) {
      return yakurekiList
    }
    const q = searchQuery.toLowerCase()
    return yakurekiList.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        (item.patient_name && item.patient_name.toLowerCase().includes(q)) ||
        (item.queue_number && item.queue_number.includes(q))
    )
  }, [searchQuery, yakurekiList])

  // 日付を中央に表示し、前日・翌日ボタンを配置するコンポネント
  const DateButton = (
    <Stack direction="row" justifyContent="space-between" width="100%">
      <IconButton onClick={handleDateBack} disabled={isBackDisabled}>
        <ArrowBackIosNewIcon
          style={{
            color: getIconColor(isBackDisabled),
          }}
        />
      </IconButton>
      <Tooltip title={isToday ? '' : '今日へ移動'} placement="top" arrow>
        <Button
          sx={{ color: DRAWER_COLORS.text }}
          onClick={isToday ? undefined : handleJumpToToday}
        >
          <Typography
            sx={{
              fontSize: 24,
            }}
          >
            {formatDateTimeToDate(date)}
          </Typography>
        </Button>
      </Tooltip>
      <IconButton onClick={handleDateForward} disabled={isForwardDisabled}>
        <ArrowForwardIosIcon
          style={{
            color: getIconColor(isForwardDisabled),
          }}
        />
      </IconButton>
    </Stack>
  )

  return (
    <>
      {/* 検索用モーダル */}
      <SearchModalBody
        isOpen={searchOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onClose={onCloseSearchModal}
        select={selectYakureki}
        yakurekiList={filteredYakurekiList}
      />

      <SettingModalBody isOpen={settingOpen} onClose={onCloseSettingModal} />

      {/* Prescriptionリストを表示するDrawer本体 */}
      <Drawer
        anchor={'left'}
        open={isOpen}
        onClose={onClose}
        sx={{
          position: 'relative',
          '& .MuiDrawer-paper': drawerStyles.paper,
        }}
        style={{
          zIndex: 1301,
        }}
      >
        <Stack direction="column" sx={{ height: '100vh', width: 250 }}>
          <Stack
            justifyContent="center"
            direction="column"
            left={0}
            top={0}
            bgcolor={DRAWER_COLORS.backgroundSolid}
            color={DRAWER_COLORS.text}
            p={1}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              width="100%"
              alignItems={'center'}
            >
              <Box>
                <Tooltip title="患者リストを更新" placement="top" arrow>
                  <IconButton onClick={handleResetYakurekiList}>
                    <RefreshIcon
                      style={{ color: DRAWER_COLORS.textSecondary }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* 通常モード: 録音ボタン → メニューで選択
                   batchDismiss / multiSelect いずれの選択モード中も非表示 (ステート競合防止) */}
                {!selection.isInSelectionMode && onRecordClick && (
                  <>
                    <Tooltip title={tooltipText} placement="top" arrow>
                      <IconButton
                        onClick={(e) => {
                          setMicMenuAnchor(e.currentTarget)
                        }}
                      >
                        <MicIcon
                          style={{ color: DRAWER_COLORS.textSecondary }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={micMenuAnchor}
                      open={isMicMenuOpen}
                      onClose={() => setMicMenuAnchor(null)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      sx={{
                        zIndex: 1400,
                        '& .MuiPaper-root': {
                          backgroundColor: DRAWER_COLORS.background,
                          boxShadow: DRAWER_COLORS.shadow,
                          border: `1px solid ${DRAWER_COLORS.borderTransparent}`,
                          borderRadius: '8px',
                          minWidth: 200,
                        },
                        '& .MuiMenuItem-root': {
                          color: DRAWER_COLORS.text,
                          fontFamily: 'inherit',
                          py: 1.2,
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        },
                        '& .MuiListItemText-primary': {
                          fontFamily: 'inherit',
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setMicMenuAnchor(null)
                          selection.enterMultiSelect()
                        }}
                      >
                        <ListItemIcon>
                          <GroupsIcon
                            fontSize="small"
                            sx={{ color: DRAWER_COLORS.textSecondary }}
                          />
                        </ListItemIcon>
                        <ListItemText>まとめて録音</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setMicMenuAnchor(null)
                          onRecordClick()
                          onClose()
                        }}
                      >
                        <ListItemIcon>
                          <PersonOffIcon
                            fontSize="small"
                            sx={{ color: DRAWER_COLORS.textSecondary }}
                          />
                        </ListItemIcon>
                        <ListItemText>フリー録音</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
                {/* 設定アイコンを押すとモーダルが開く */}
                <Tooltip title="設定" placement="top" arrow>
                  <IconButton onClick={handleSettingOpen}>
                    <TuneIcon style={{ color: DRAWER_COLORS.textSecondary }} />
                  </IconButton>
                </Tooltip>
                {/* 検索アイコンを押すとモーダルが開く */}
                <Tooltip title="患者を検索" placement="top" arrow>
                  <IconButton onClick={handleSearchOpen}>
                    <SearchIcon
                      style={{ color: DRAWER_COLORS.textSecondary }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
            {DateButton}
            {!isToday && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  size="small"
                  onClick={handleJumpToToday}
                  sx={{
                    color: colorTokens.blue[600],
                    py: 0,
                    minHeight: 'auto',
                  }}
                >
                  今日へ移動
                </Button>
              </Box>
            )}
          </Stack>
          {/* マルチ選択モード時の操作バー（ヘッダー固定） */}
          {selection.isMultiSelect && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                px: 2,
                py: 0.5,
                bgcolor: DRAWER_COLORS.backgroundSolid,
                borderBottom: `1px solid ${DRAWER_COLORS.borderTransparent}`,
              }}
            >
              <Button
                size="small"
                onClick={selection.exit}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  color: DRAWER_COLORS.textSecondary,
                }}
              >
                キャンセル
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={selection.selectionCount < 2}
                onClick={() => {
                  onStartGroupRecording()
                  onClose()
                }}
                startIcon={<MicIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  bgcolor: (theme) => theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.primary.dark,
                  },
                  '&:disabled': {
                    bgcolor: DRAWER_COLORS.iconDisabled,
                    color: '#fff',
                  },
                }}
              >
                まとめて録音
              </Button>
            </Stack>
          )}
          <Box
            bgcolor={DRAWER_COLORS.background}
            color={DRAWER_COLORS.text}
            sx={{
              flex: 1,
              overflowY: 'scroll',
              overflowX: 'hidden',
              scrollbarWidth: 10,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              // 薬剤師未選択時はグレーアウト
              ...(!isPharmacistSelected && {
                opacity: 0.4,
                pointerEvents: 'none',
              }),
            }}
            role="presentation"
          >
            {/* 薬剤師未選択時の案内 */}
            {!isPharmacistSelected && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.5,
                  textAlign: 'center',
                  borderBottom: `1px solid ${DRAWER_COLORS.border}`,
                  opacity: 1,
                  pointerEvents: 'auto',
                }}
              >
                <Typography
                  sx={{
                    color: DRAWER_COLORS.textSelected,
                  }}
                >
                  薬剤師を選択してください
                </Typography>
              </Box>
            )}
            {/* 受付順ラベル + まとめて完了ボタン */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${DRAWER_COLORS.border}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: DRAWER_COLORS.textSecondary,
                }}
              >
                受付順
              </Typography>
              {!selection.isMultiSelect &&
                (selection.isBatchDismiss ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Button
                      size="small"
                      onClick={selection.exit}
                      sx={{
                        minWidth: 'auto',
                        px: 0.5,
                        py: 0,
                        color: DRAWER_COLORS.textSecondary,
                      }}
                    >
                      キャンセル
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={selection.selectionCount === 0}
                      onClick={() => {
                        if (
                          window.confirm(
                            `${selection.selectionCount}件を完了にしますか？`
                          )
                        ) {
                          onConfirmBatchDismiss(
                            Array.from(selection.selectedIds)
                          )
                        }
                      }}
                      sx={{
                        minWidth: 'auto',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {selection.selectionCount}件完了
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selection.enterBatchDismiss}
                    sx={drawerButtonStyle}
                  >
                    まとめて完了
                  </Button>
                ))}
            </Stack>
            <List sx={{ py: 0 }}>
              {dateFilteredYakurekiList.map((yakureki) => {
                const isItemSelected = selection.isInSelectionMode
                  ? selection.isSelected(yakureki.id)
                  : selectedYakureki
                  ? yakureki.id === selectedYakureki.id
                  : false
                return (
                  <Box
                    key={yakureki.id}
                    ref={isItemSelected ? selectedItemRef : undefined}
                  >
                    <DrawerItem
                      yakureki={yakureki}
                      selectYakureki={
                        selection.isInSelectionMode
                          ? selection.togglePatient
                          : selectYakureki
                      }
                      isSelected={isItemSelected}
                      isProgress={isInProgressStatus(yakureki.status)}
                      showCheckbox={selection.isInSelectionMode}
                      isCheckboxDisabled={
                        selection.isBatchDismiss
                          ? yakureki.status === 'dismissed'
                          : selection.isMultiSelect
                          ? !isSelectableForGroup(yakureki)
                          : false
                      }
                      onMarkDone={handleMarkDone}
                      onUndoDismiss={handleUndoDismiss}
                    />
                  </Box>
                )
              })}
            </List>
          </Box>
          {/* 薬剤師フィルタ + コピー済み非表示トグル（下部に配置） */}
          <Box
            bgcolor={DRAWER_COLORS.backgroundSolid}
            sx={{
              borderTop: `1px solid ${DRAWER_COLORS.borderTransparent}`,
              px: 2,
              py: 1,
            }}
          >
            {/* 薬剤師フィルタ */}
            {pharmacistData && pharmacistData.pharmacists.length > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: DRAWER_COLORS.text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {PHARMACIST_FILTER_LABEL}
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={filterPharmacistId}
                    onChange={(e) =>
                      handlePharmacistFilterChange(e.target.value as string)
                    }
                    MenuProps={{
                      sx: { zIndex: 1400 },
                    }}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.7)',
                      '& .MuiSelect-select': {
                        py: 0.75,
                      },
                    }}
                  >
                    <MenuItem value={PHARMACIST_FILTER_ALL_VALUE}>
                      全員
                    </MenuItem>
                    {pharmacistData.pharmacists.map((pharmacist) => (
                      <MenuItem key={pharmacist.id} value={pharmacist.id}>
                        {pharmacist.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}
            {/* 完了表示トグル */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1,
                py: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: DRAWER_COLORS.text,
                }}
              >
                {SHOW_DISMISSED_LABEL}
                {dismissedCount > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      color: DRAWER_COLORS.textSecondary,
                      ml: 0.5,
                    }}
                  >
                    ({dismissedCount})
                  </Typography>
                )}
              </Typography>
              <Switch
                checked={showDismissed}
                onChange={handleShowDismissedToggle}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase': {
                    padding: '6px',
                    '&.Mui-checked': {
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: DRAWER_COLORS.textSelected,
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: DRAWER_COLORS.textSecondary,
                    opacity: 0.3,
                    borderRadius: '20px',
                  },
                  '& .MuiSwitch-thumb': {
                    width: 16,
                    height: 16,
                  },
                }}
              />
            </Stack>
          </Box>
          {/* BottomButtons を描画 */}
          {BottomButtons.length > 0 && (
            <Box
              bgcolor={DRAWER_COLORS.backgroundSolid}
              color={DRAWER_COLORS.text}
              p={1}
              sx={{
                borderTop: `1px solid ${DRAWER_COLORS.borderTransparent}`,
              }}
            >
              <Stack spacing={1}>
                {BottomButtons.map((button, index) => (
                  <Box key={`bottom-button-${index}`}>{button}</Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Drawer>
    </>
  )
}

type DrawerProps = {
  yakureki: Yakureki
  selectYakureki: (yakurekiId: string) => void
  isSelected: boolean
  isProgress: boolean
  showCheckbox?: boolean
  isCheckboxDisabled?: boolean
  onMarkDone?: (yakurekiId: string) => void
  onUndoDismiss?: (yakurekiId: string) => void
}

// 2行目のサブテキスト（薬剤師名、録音前は空白）
const getSubLabel = (yakureki: Yakureki): string => {
  return yakureki.pharmacist_name ?? '\u00A0'
}

// 患者の表示名を取得（queue_number + patient_name があれば分離、なければ label フォールバック）
const hasStructuredName = (yakureki: Yakureki): boolean =>
  !!(yakureki.queue_number && yakureki.patient_name)

// 受付番号の表示スタイル（Inter等幅数字、固定幅で縦揃え）
const queueNumberStyle = {
  fontFamily: "'Inter', sans-serif",
  fontFeatureSettings: "'tnum'",
  minWidth: 24,
  textAlign: 'right' as const,
  flexShrink: 0,
} as const

// 患者名の表示スタイル（姓名間 0.3em スペーシング）
const patientNameStyle = {
  wordSpacing: '0.3em',
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
  whiteSpace: 'nowrap' as const,
  flex: 1,
  minWidth: 0,
} as const

const DrawerItem: NamedExoticComponent<DrawerProps> = memo(
  ({
    yakureki,
    selectYakureki,
    isSelected,
    isProgress,
    showCheckbox,
    isCheckboxDisabled,
    onMarkDone,
    onUndoDismiss,
  }) => {
    const structured = hasStructuredName(yakureki)

    // 通常表示（isProgress 含む — 2行目のアイコンで状態を区別）
    return (
      <ListItemButton
        onClick={() => {
          if (showCheckbox && isCheckboxDisabled) return
          selectYakureki(yakureki.id)
        }}
        key={yakureki.id}
        sx={{
          ...drawerStyles.listItem(isSelected),
          ...(showCheckbox && isCheckboxDisabled
            ? { opacity: 0.5, cursor: 'default' }
            : {}),
          py: 1.25,
          px: 1.5,
        }}
      >
        <Stack width="100%" spacing={0.5}>
          {/* 1行目: 受付番号 + 患者名 */}
          <Stack direction="row" spacing={0.75} alignItems="center">
            {structured ? (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    ...queueNumberStyle,
                    fontWeight: 600,
                    color: isSelected
                      ? DRAWER_COLORS.textSelected
                      : DRAWER_COLORS.text,
                  }}
                >
                  {yakureki.queue_number}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    ...patientNameStyle,
                    fontWeight: 600,
                  }}
                >
                  {yakureki.patient_name}
                </Typography>
              </>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {yakureki.label}
              </Typography>
            )}
          </Stack>
          {/* 2行目: ステータス + サブラベル（左） + 完了/戻すボタン（右） */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ minHeight: 20 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isProgress ? (
                <CircularProgress
                  size={12}
                  sx={{
                    color: DRAWER_COLORS.textSelected,
                    flexShrink: 0,
                  }}
                />
              ) : yakureki.status === 'copied' ? (
                <DoneIcon
                  sx={{
                    fontSize: 14,
                    flexShrink: 0,
                    color: isSelected
                      ? DRAWER_COLORS.textSelected
                      : DRAWER_COLORS.textSecondary,
                  }}
                />
              ) : (
                yakureki.has_audio && (
                  <MicIcon
                    sx={{
                      fontSize: 14,
                      flexShrink: 0,
                      color: isSelected
                        ? DRAWER_COLORS.textSelected
                        : DRAWER_COLORS.textSecondary,
                    }}
                  />
                )
              )}
              <Typography
                variant="caption"
                sx={{
                  color: DRAWER_COLORS.textSecondary,
                  lineHeight: 1.2,
                }}
              >
                {isProgress ? '作成中' : getSubLabel(yakureki)}
              </Typography>
            </Stack>
            {/* まとめて完了/録音モード時: チェックボックスを右側に表示 */}
            {showCheckbox && (
              <Checkbox
                checked={isSelected}
                disabled={isCheckboxDisabled}
                size="small"
                sx={{ p: 0, flexShrink: 0 }}
              />
            )}
            {/* 完了 / 戻すボタン */}
            {!showCheckbox &&
              yakureki.status === 'dismissed' &&
              onUndoDismiss && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation()
                    onUndoDismiss(yakureki.id)
                  }}
                  sx={drawerButtonStyle}
                >
                  戻す
                </Button>
              )}
            {!showCheckbox &&
              yakureki.status !== 'dismissed' &&
              yakureki.status !== 'processing' &&
              onMarkDone && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (window.confirm('この患者を完了にしますか？')) {
                      onMarkDone(yakureki.id)
                    }
                  }}
                  sx={drawerButtonStyle}
                >
                  完了
                </Button>
              )}
          </Stack>
        </Stack>
      </ListItemButton>
    )
  },
  (prev, next) => {
    return (
      prev.yakureki.label === next.yakureki.label &&
      prev.yakureki.queue_number === next.yakureki.queue_number &&
      prev.yakureki.patient_name === next.yakureki.patient_name &&
      prev.yakureki.created_at === next.yakureki.created_at &&
      prev.yakureki.is_create_from_nsips ===
        next.yakureki.is_create_from_nsips &&
      prev.yakureki.has_audio === next.yakureki.has_audio &&
      prev.yakureki.status === next.yakureki.status &&
      prev.yakureki.pharmacist_name === next.yakureki.pharmacist_name &&
      prev.isSelected === next.isSelected &&
      prev.isProgress === next.isProgress &&
      prev.showCheckbox === next.showCheckbox &&
      prev.isCheckboxDisabled === next.isCheckboxDisabled &&
      prev.onMarkDone === next.onMarkDone &&
      prev.onUndoDismiss === next.onUndoDismiss
    )
  }
)

DrawerItem.displayName = 'DrawerItem'
